import { getToken, reauthenticateWithMfa } from './auth';

export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string) {
    super(message);
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  const token = await getToken();
  const res = await fetch(`/api${url}`, {
    method,
    headers: { Authorization: `Bearer ${token}`, ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = res.headers.get('content-type')?.includes('json') ? await res.json() : null;
  if (res.status === 401 && data?.code === 'mfa_required') {
    await reauthenticateWithMfa();
  }
  if (!res.ok) throw new ApiError(res.status, data?.error ?? `Eroare ${res.status}`, data?.code);
  return data as T;
}

export const api = {
  get: <T,>(url: string) => request<T>('GET', url),
  post: <T,>(url: string, body: unknown) => request<T>('POST', url, body),
  put: <T,>(url: string, body: unknown) => request<T>('PUT', url, body),
  del: <T,>(url: string) => request<T>('DELETE', url),
};

// ---------------- Tipuri ----------------
export interface Me {
  id: number; email: string; displayName: string;
  authMethod: 'microsoft' | 'password'; mfa?: boolean; mustChangePassword: boolean; allowPasswordLogin: boolean; hasAvatar: boolean;
  systemRoles: string[]; systemPermissions: string[];
}
export interface Permission { id: number; code: string; description: string; scope: 'System' | 'Workspace' }
export interface Role {
  id: number; code: string; name: string; description: string | null;
  scope: 'System' | 'Workspace'; isBuiltIn: boolean; permissionIds: number[];
}
export interface User {
  id: number; email: string; displayName: string; jobTitle: string | null; phone: string | null;
  supervisorId: number | null; isActive: boolean; lastLoginAt: string | null; roleIds: number[]; overrideCount: number;
  allowPasswordLogin: boolean; hasPassword: boolean; hasMicrosoft: boolean; mustChangePassword: boolean; lockedUntil: string | null; hasAvatar: boolean;
}
export interface Override {
  permissionId: number; workspaceId: number | null; effect: 'Allow' | 'Deny';
  reason: string | null; validUntil: string | null;
}
export interface WorkspaceSummary {
  id: number; name: string; slug: string; description: string | null; privacy: 'Open' | 'Closed' | 'Private';
  color: string | null; myRole: string | null; myRoleName: string | null; memberCount: number; appCount: number;
}
export interface WorkspaceDetail {
  id: number; name: string; slug: string; description: string | null; privacy: 'Open' | 'Closed' | 'Private';
  color: string | null; myPermissions: string[];
  apps: { id: number; name: string; itemName: string; slug: string; icon: string | null; itemCount: number; isPinned: boolean }[];
  members: { userId: number; displayName: string; email: string; roleId: number; roleCode: string; roleName: string }[];
}
export interface DirectoryUser { id: number; displayName: string; email: string }
export interface AdminWorkspace {
  id: number; name: string; slug: string; description: string | null; privacy: 'Open' | 'Closed' | 'Private';
  color: string | null; sortOrder: number; isArchived: boolean; createdAt: string; memberCount: number; appCount: number;
}
export interface ResourceRow {
  id: string; name: string; type: string; typeName: string; location: string; resourceGroup: string;
  sku: string | null; costMonthToDate: number; environment: string | null;
}
export interface ResourcesSummary {
  currency: string; costMonthToDate: number; costError: string | null; costNote: string;
  budget: { name: string; amount: number; currentSpend: number | null } | null;
  resources: ResourceRow[]; updatedAt: string;
}
export interface LiveMetric { name: string; label: string; unit: string; last: number | null; points: { t: string; v: number | null }[] }
export interface LiveItem { id: string; name: string; kind: string; metrics: LiveMetric[] }

/** Descarcă un fișier binar cu autentificare (ex. poze de profil) și întoarce un URL local. */
export async function fetchBlobUrl(url: string): Promise<string | null> {
  const token = await getToken();
  const res = await fetch(`/api${url}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) return null;
  return URL.createObjectURL(await res.blob());
}

/** Încărcare fișier (corp binar) — pentru documente. */
export async function uploadFile<T>(url: string, file: Blob, fileName: string, extraQuery = ''): Promise<T> {
  const token = await getToken();
  const res = await fetch(`/api${url}?name=${encodeURIComponent(fileName)}${extraQuery}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  const data = res.headers.get('content-type')?.includes('json') ? await res.json() : null;
  if (!res.ok) throw new ApiError(res.status, data?.error ?? `Eroare ${res.status}`);
  return data as T;
}

// ---------------- Clienți ----------------
export type DocTip = 'CN' | 'CI' | 'CEI' | 'CAD' | 'PC' | 'PAS' | 'AD';
export type DocCategorie = 'CN' | 'CI' | 'CAD' | 'PC' | 'PAS' | 'AD';
export interface ClientRow {
  id: number; nume: string; prenume: string | null; telefon: string | null; email: string | null;
  localitate: string | null; judet: string | null; documente: number; primaExpirare: string | null; tipClient: 'PF' | 'PJ';
}
export interface ClientFields {
  nume: string; prenume: string | null; numeAnterior: string | null; telefon: string | null; email: string | null;
  strada: string | null; numar: string | null; bloc: string | null; scara: string | null; etaj: string | null; apartament: string | null;
  localitate: string | null; judet: string | null; codPostal: string | null; alteDetaliiAdresa: string | null;
  tipStrada: string | null; responsabilUserId: number | null;
  dataNasterii: string | null; birthdayReminder: boolean; observatii: string | null;
}
export interface ClientDetail extends ClientFields {
  id: number; appId: number; tipClient: 'PF' | 'PJ'; cnp: string | null; hasCnp: boolean; cnpValid: boolean | null; cnpBirthDate: string | null; canSeeCnp: boolean;
  /** versiunea fișei (protecție la editări simultane) */
  rowVer: string | null; updatedAt: string | null;
}
export interface ClientContact { id?: number; tip: 'Telefon' | 'Email'; valoare: string; eticheta: string | null }
export interface ClientVersion {
  at: string; by: string | null; current: boolean; first: boolean; changed: string[];
  client: ClientDetail; contacts: ClientContact[];
}
export interface ClientHistory {
  versions: ClientVersion[];
  deletedDocuments: (ClientDoc & { deletedAt: string | null; deletedBy: string | null })[];
  canRestore: boolean;
}
export interface ClientDoc {
  id: number; clientId: number | null; tipDocument: DocTip; categorie: DocCategorie; denumireDocument: string | null;
  versiune: number; isCurrent: boolean; replacedAt: string | null; downloadName: string; serie: string | null; numar: string | null; emisDe: string | null;
  dataEmiterii: string | null; dataExpirarii: string | null; fileName: string; contentType: string; sizeBytes: number; uploadedAt: string;
}
export interface Extracted {
  tipDocument: DocTip; nume?: string; prenume?: string; cnp?: string; dataNasterii?: string; sex?: string;
  serie?: string; numar?: string; emisDe?: string; dataEmiterii?: string; dataExpirarii?: string;
  adresa?: { tipStrada?: string; strada?: string; numar?: string; bloc?: string; scara?: string; etaj?: string; apartament?: string; localitate?: string; judet?: string; codPostal?: string };
  avertismente: string[];
}
export interface UploadResult { document: ClientDoc; extracted: Extracted | null; extractError?: string }
